<template>
  <div
      :class="className"
      :style="styleColor"
      :id="disabled ? '' : room.id"
      class="ui-chess-item"
      @mouseleave="closeActionModal"
      @mousemove="openActionModal"
      v-on="$listeners"
  >
    <template v-if="!disabled">
      <div
          v-if="!onlyStatus"
          :class="{'full-height': !roomStatus && isSizeS}"
          class="ui-chess-item-header"
      >
        <div
            v-if="roomName && isSizeL"
            key="roomName"
            class="ui-chess-item-name"
        >
          {{ roomName }}
        </div>
        <div
            v-if="roomNumber"
            class="ui-chess-item-number">
          {{ roomNumber }}
        </div>
        <p v-if="roomStatus && isSizeM">{{ roomStatus }}</p>
      </div>
      <p
          v-if="roomStatus && isSizeS"
          class="ui-chess-item-status">
        {{ roomStatus }}
      </p>
      <p
          v-if="roomName && isSizeM && !onlyStatus"
          key="roomName"
          class="ui-chess-item-name"
      >{{ roomName }}</p>
      <template v-if="isSizeL && !onlyStatus">
        <p
            v-if="roomPrice"
            class="ui-chess-item-price"
            key="roomPrice">
          {{ roomPrice }}
        </p>
        <p
            v-if="roomAreaAndPrice"
            key="roomAreaAndPrice"
            class="ui-chess-item-area-price"
        >
          {{ roomAreaAndPrice }}
        </p>
      </template>
      <p
          v-if="roomComment && !isSizeS && !onlyStatus && hasRoomComment"
          key="roomComment"
          class="ui-chess-item-comment"
      >{{ roomComment }}</p>
      <template v-if="hasGrades">
        <ui-chess-item-grades
          class="ui-chess-item__grades"
          :grades="grades"
        />
        <div class="ui-chess-item__round-status" :style="styleColor" />
      </template>
    </template>
  </div>
</template>

<script>
import UiChessItemGrades from './UiChessItemGrades.vue'

import { getCurrency, shaderColor } from '../../libs/helpTools'
import { COMPONENT_SIZE } from '../../libs/helpConsts'

export default {
  name: 'UiChessItem',
  components: { UiChessItemGrades },
  props: {
    size: {
      type: String,
      default: 's',
      validator (size) {
        return COMPONENT_SIZE.includes(size)
      }
    },

    room: {
      type: Object,
      default: () => ({})
    },

    hasColorStatus: {
      type: Boolean,
      default: true
    },

    hasRoomCount: {
      type: Boolean,
      default: true
    },

    active: {
      type: Boolean,
      default: false
    },

    onlyStatus: {
      type: Boolean,
      default: false
    },

    disabled: {
      type: Boolean,
      default: false
    },

    hasRoomComment: {
      type: Boolean,
      default: false
    },

    hasGrades: Boolean
  },

  computed: {
    isSizeS () {
      return this.size === 's'
    },

    isSizeM () {
      return this.size === 'm'
    },

    isSizeL () {
      return this.size === 'l'
    },

    className () {
      return {
        [this.size]: true,
        disabled: this.disabled,
        active: this.active,
        'only-status': this.onlyStatus,
        'room-comment': this.hasRoomComment,
        _grades: this.hasGrades
      }
    },

    styleColor () {
      if (!this.hasColorStatus) return
      return {
        backgroundColor: this.room.bg_color,
        borderColor: this.room.bg_color ? shaderColor(this.room.bg_color, -5) : null,
        color: this.room.text_color
      }
    },

    roomName () {
      return `${this.isSizeL && this.room.room_count ? this.room.room_count + ' ' : ''}${this.room.type}`
    },

    roomPrice () {
      return getCurrency(this.room.price)
    },

    roomNumber () {
      if (this.hasRoomCount && !this.isSizeL) return this.room.room_count || ''
      if (!this.room.number) return this.isSizeS ? '' : '–'
      return `${this.isSizeL ? '№' : ''}${this.room.number}`
    },

    roomStatus () {
      return this.room.label
    },

    roomComment () {
      return this.room.comment
    },

    roomAreaAndPrice () {
      const { area, price } = this.room
      if (!area || !price) return
      return `${area}м² - ${(price / area).toFixed(0)} ₽/м²`
    },

    grades () {
      return this.room.grades ?? []
    }
  },

  methods: {
    openActionModal (event) {
      if (this.disabled) return
      this.$emit('open-action-modal', {
        room: this.room,
        event
      })
    },

    closeActionModal () {
      if (this.disabled) return
      this.$emit('close-action-modal')
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-chess-item {
  $parent: &;

  @include font-14;

  position: relative;
  width: 38px;
  height: 38px;
  box-sizing: border-box;
  padding: 7px;
  color: $gray-2;
  background-color: $gray-7;
  border: 1px solid $gray-5;
  border-radius: $border-radius-01;
  white-space: nowrap;
  cursor: pointer;
  transition: 0.3s;

  p {
    color: inherit;
  }

  @include children {
    margin-bottom: 5px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &.s {
    padding: 3px;
    border-radius: 2px;

    @include children {
      margin-bottom: 0;
    }

    #{$parent}-status {
      @include font-12;
      line-height: 12px;
      text-align: right;
    }

    #{$parent}-header {
      justify-content: center;
      height: 20px;
    }

    #{$parent}__round-status {
      height: 6px;
      width: 6px;
    }

    #{$parent}__grades {
      height: 5px;
      bottom: -1px;
      border-bottom-left-radius: 2px;
      border-bottom-right-radius: 2px;
    }
  }

  &.room-comment {
    &.m {
      height: 69px;
    }

    &.l {
      height: 87px;
    }

    &.s {
      @include children {
        margin-bottom: 0;
      }
    }

    @include children {
      margin-bottom: 2px;
    }
  }

  &.m {
    width: 85px;
    height: 57px;
  }

  &.l {
    width: 160px;
    height: 81px;

    #{$parent}-number {
      flex-shrink: 0;
    }
  }

  &._grades {
    color: $gray-2 !important;
    border-color: $gray-5 !important;
    background: $white !important;

    &.s {
      width: 40px;
      height: 40px;
    }

    &.m {
      width: 87px;
      height: 59px;
    }

    &.l {
      width: 162px;
      height: 83px;
    }
  }

  &.active {
    color: $white !important;
    border-color: $blue-2 !important;
    background-color: $blue-2 !important;
  }

  &.disabled {
    background-color: $gray-7 !important;
    border-color: $gray-7 !important;
    cursor: default;
  }

  &.only-status {
    @include flex-center;

    .ui-chess-item-status {
      @include font-14;
      font-weight: 500;
    }
  }

  & &-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &.full-height {
      height: 100%;
    }

    p {
      margin-bottom: 0;
    }
  }

  &-comment {
    @include ellipsis;
    @include font-12;
  }

  &-number,
  &-price {
    font-weight: 500;
  }

  &-name,
  &-number,
  &-status {
    @include ellipsis;
  }

  &-area-price {
    @include getFontSize(13);
  }

  &__grades {
    position: absolute;
    bottom: -1px;
    left: -1px;
    width: calc(100% + 2px);
    margin: 0;
    border-bottom-left-radius: $border-radius-01;
    border-bottom-right-radius: $border-radius-01;
    overflow: hidden;
  }

  &__round-status {
    position: absolute;
    top: 1px;
    right: 1px;
    height: 8px;
    width: 8px;
    border-radius: 50%;
  }
}
</style>
